import { Component, Input, Output, OnInit, OnDestroy, EventEmitter, Inject } from "@angular/core";
import { Subject, Observable } from "rxjs";
import { NmsCounterModel } from "@nms-ng2/app/shared/components/elements/nms-counter/nms-counter.component";
import { WarningSettings } from "../nms-warning/nms-warning.models";
import { ANGULARJS_TRANSLATE } from "@nms-ng2/app/shared/services/upgraded-provider/upgraded-providers";
import { TranslatedText, TranslationKey } from "@nms-ng2/app/shared/models/translation.models";
import { PRIMARY_DANGER_COLOR, PRIMARY_PRESSED_COLOR } from "@nms-ng2/app/shared/constants/colors";
import { NmsAutoUpdaterRegistryService } from "./nms-auto-updater-registry.service";

export interface AutoUpdaterDataResult {
    data?: any;
    error?: any;
}

/**
 * Representa os detalhes de um update para ser exibido no tooltip
 */
export interface UpdateDetail {
    title: string;
    subtitle: string;
    success: boolean;
}

/** Componente para realizar auto atualização. */
@Component({
    selector: "nms-auto-updater",
    templateUrl: "./nms-auto-updater.component.html",
    styleUrls: ["./nms-auto-updater.component.scss"]
})
export class NmsAutoUpdaterComponent implements OnInit, OnDestroy {

    /** Identificador do componente */
    @Input()
    id: string;
    /** Detalhes referente ao update */
    @Input()
    lastUpdateDetails: Array<UpdateDetail>;
    /** Chave de tradução para contagem de tempo do nms-auto-updater */
    @Input()
    retrieveMessageTranslationKey: TranslationKey;
    /** Chave de tradução para o botão de atualização do nms-auto-updater */
    @Input()
    retrieveDataButtonTranslationKey: TranslationKey;
    /** Chave de tradução para o text de atualização da página do nms-auto-updater */
    @Input()
    updateDataLabelTranslationKey: TranslationKey;
    /** Função que trará dados para atualização. Ela deve retornar um @see Observable. */
    @Input()
    dataRetrieveProvider: () => Observable<any>;
    /** Intervalo de tempo entre as atualizações em segundos. Default: 300 segundos (5 min) */
    @Input()
    intervalTime?: number = 300;
    /** Warnings e suas configurações de exibição */
    @Input()
    warningSettings?: WarningSettings;
    /** Mensagem customizada que será exibida quando o contador for pausado */
    @Input()
    customPausedMessage?: string;

    /**
     * Indica ao componente se ele deve ou não chamar o dataRetrieveProvider ao iniciar o componente.
     * Caso o valor seja 'true' o componente chamará o método, e notificará o responsável sobre os dados recebidos.
     * Caso o valor seja 'false' o componente não chamará o método responsável por buscar os dados.
     * Este tratamento é especialmente útil nos casos em que o nms-auto-updater é utilizado em conjunto com o nms-table,
     * neste caso, o valor false indica que os dados serão inicialmente carregados pela tabela e não pelo auto-updater.
     *
     * @default true
     */
    @Input()
    updateOnStartup?: boolean = true;

    /** Evento para enviar o resultado dos dados recuperados para o componente pai. */
    @Output()
    onDataReceived = new EventEmitter<AutoUpdaterDataResult>();

    /** Notifier para início de atualização */
    startNotifier: Subject<NmsCounterModel>;
    /** Notifier para fim de atualização */
    stopNotifier: Subject<NmsCounterModel>;
    /** Notifier para pausa de atualização */
    pauseNotifier: Subject<boolean>;
    /** Notifier para início de tempo ocioso */
    idleStartNotifier: Subject<NmsCounterModel>;
    /** Notifier para fim de tempo ocioso */
    idleStopNotifier: Subject<NmsCounterModel>;
    /** Indica se a atualização está em progresso */
    isUpdating: boolean;
    /** Indica se o contador está pausado */
    isPaused: boolean;
    /** Indica se o processo de auto atualização foi iniciado pelo componente pai. */
    isInitialized: boolean;
    /** Informação de última atualização. Na inicialização do componente, enquanto não há dados
     * terá como valor a mensagem: "Por favor, aguarde...".
     * Quando houver update será sobrescrita com a mensagem: "Última atualização: DD/MM - HH:mm"
     */
    lastUpdateInfo: TranslatedText;

    constructor(@Inject(ANGULARJS_TRANSLATE) private readonly translate: any,
            private readonly registryService: NmsAutoUpdaterRegistryService) {
        this.isUpdating = false;
        this.startNotifier = new Subject();
        this.stopNotifier = new Subject();
        this.pauseNotifier = new Subject();
        this.idleStartNotifier = new Subject();
        this.idleStopNotifier = new Subject();
    }

    ngOnInit(): void {
        if (this.id) {
            this.registryService.register(this.id, this);
        }

        this.retrieveMessageTranslationKey = this.retrieveMessageTranslationKey || "nms.auto.updater.updating.message";
        this.retrieveDataButtonTranslationKey = this.retrieveDataButtonTranslationKey || "nms.auto.updater.button.label";
        this.updateDataLabelTranslationKey = this.updateDataLabelTranslationKey || "nms.auto.updater.waiting.update.message";
        this.lastUpdateInfo = this.translate.instant("nms.auto.updater.please.wait");
        this.isInitialized = true;

        if (this.updateOnStartup) {
            setTimeout(() => {
                this.start();
            }, 0);
        } else {
            this.lastUpdateInfo = this.translate.instant("nms.auto.updater.update.now.or.wait");
            this.startCountdown();
        }
    }

    /**
     * Inicia o processo de atualização, notificando o contador de atualização e realizando a requisição dos dados.
     */
    start = (): void => {
        this.isUpdating = true;
        this.startNotifier.next({ countStart: 0 });
        this.continue();
        this.lastUpdateInfo = this.translate.instant("nms.auto.updater.please.wait");
        this.dataRetrieveProvider().subscribe(
            (data) => this.notifyDataReceived({ data: data }),
            (error) => this.notifyDataReceived({ error: error })
        );
    }

    /**
     * Pausa o processo de cronometragem.
     */
    pause = (): void => {
        this.isPaused = true;
        this.pauseNotifier.next(true);
    }

    /**
     * Continua com o processo de cronometragem.
     */
    continue = (): void => {
        this.isPaused = false;
        this.pauseNotifier.next(false);
    }

    /**
     * Finaliza a atualização, notificando fim para o contador de atualização e o iníco para contador de tempo ocioso.
     */
    stop = (): void => {
        this.isUpdating = false;
        this.stopNotifier.next();
        this.idleStartNotifier.next({ countStart: this.intervalTime });
    }

    /**
     * Finaliza a contagem de tempo ocioso, notificando seu contador e iniciando uma nova atualização.
     */
    stopIdleAndRestart = (): void => {
        this.idleStopNotifier.next();
        this.start();
    }

    /**
     * Obtém a cor do ícone.
     * @returns Cor do ícone conforme a presença de erros
     */
    getIconColor(): string {
        return this.getMessageColor(this.hasError());
    }

    /**
     * Avalia se existe erro em algum dos detalhes.
     */
    hasError(): boolean {
        return this.lastUpdateDetails?.some(detail => !detail.success);
    }

    /**
     * Retorna as cores default do sistema para sucesso e falha;
     * @param hasFail
     * @returns - cor para sucesso e falha conforme argumento
     */
    getMessageColor (hasFail: boolean): string {
        return hasFail ? PRIMARY_DANGER_COLOR : PRIMARY_PRESSED_COLOR;
    }

    ngOnDestroy(): void {
        this.startNotifier.unsubscribe();
        this.stopNotifier.unsubscribe();
        this.idleStartNotifier.unsubscribe();
        this.idleStopNotifier.unsubscribe();

        if (this.id) {
            this.registryService.delete(this.id);
        }
    }

    /**
     * Notifica o recebimento dos dados e finaliza a atualização.
     */
    private notifyDataReceived(response: AutoUpdaterDataResult) {
        if (response.data) {
            this.lastUpdateInfo = this.generateLastUpdateText();
        }
        this.onDataReceived.emit(response);
        this.stop();
    }

    /**
     * Gera a mensagem traduzida da última atualização.
     * Ex.: Última atualização: 27/01 - 14:03
     * @returns mensagem de última atualização contendo dia, mês, hora e minutos
     */
    private generateLastUpdateText(): TranslatedText {
        const lastUpdateLabel = this.translate.instant("nms.auto.updater.last.update");
        const now = new Date();
        const hours = this.numberToTwoDigitsString(now.getHours());
        const minutes = this.numberToTwoDigitsString(now.getMinutes());

        return lastUpdateLabel
            .replace("{dd}", this.numberToTwoDigitsString(now.getDate()))
            .replace("{mm}", this.numberToTwoDigitsString(now.getMonth() + 1))
            .replace("{time}", `${hours}:${minutes}`);
    }

    private numberToTwoDigitsString(value: number) {
        return String(value).padStart(2, "0");
    }

    /**
     * FIXME [US-26314]: Método temporário para iniciar o contador em modo regressivo sem disparar a busca de dados
     * durante o carregamento.
     * Este é um método provisório para evitar que, em casos onde o nms-table é utilizado em conjunto com o nms-auto-updater,
     * o endpoint de busca de dados seja acionado duas vezes. O ideal é tratar o componente para que ele tenha esse comportamento
     * e não seja necessário a chamada de um setTimeout.
     */
    private startCountdown() {
        this.startNotifier.next({ countStart: 0 });
        this.continue();
        setTimeout(() => this.stop(), 300);
    }
}
